import { formatDateDifference } from 'src/cer/cer-util/cer-date';
import { UiActionTypeEnum, UiCommand, UiCommandEvent } from '@ui/cer-ui-types';
import { ColorCss, FieldWidthEnum } from 'src/cer/cer-data/cer-data-types';
import { CerFormAdapter } from '@cer/cer-form/cer-form-adapter';
import { CerFormView } from '@cer/cer-form/cer-form-view';
import { CerGridComponent } from '@cer/cer-grid/cer-grid.component';

const updatedDateCmd: UiCommand = {
  id: 'updatedDate', text: 'Opdateret: ', disabled: true, align: 'right',
  iconCss: "e-menu-icon e-icons e-circle-check", tooltipText: 'Dato hvor data er opdateret', target: ".e-content", visible: true
};

export class WarehouseOnhandStatusForm extends CerFormAdapter {
  override view(): CerFormView {
    return new CerFormView({
      toolbarCommands: [updatedDateCmd],
      onCommandEvent: (e => this.onCommand(e)),
      viewMetadata: {
        name: 'lagerbeholdninger', dataApiName: 'WarehouseOnhandProductStatus', text: 'Lagerbeholdninger', textSingular: 'Lagerbeholdning',
        primaryKey: 'product', allowEdit: false, allowCreate: false, allowDelete: false,
        navigations: [
          { link: 'navMenu', foreignKey: { parentEntity: 'WarehouseOutboundBackOrderOnHandProductStatus', parentColumn: 'product', column: 'product' } },
          { link: 'navMenu', foreignKey: { parentEntity: 'WarehousePurchLine', parentColumn: 'productNum', column: 'num' } }
        ]
      },
      fieldMetadata: [
        { name: 'product', text: 'ISBN', width: '120px',  foreignKeyType: 'productLookup' },
        { name: 'projectNum', text: 'Projekt', width: '90px', visible: false },
        { name: 'itemNum', text: 'Varenr.', width: '90px', visible: false },
        { name: 'contractType', text: 'Kontrakttype', width: '90px', visible: false },
        { name: 'settlementType', text: 'Afregningstype', width: '90px', visible: false },
        { name: 'name', text: 'Titel', width: '150px' },
        { name: 'name2', text: 'Undertitel', visible: false },
        { name: 'responsible', text: 'Redaktør', width: '90px', visible: false },
        { name: 'group', text: 'Gruppe', width: '90px', visible: false, foreignKeyType: 'productGroup' },
        { name: 'seriesId', text: 'Serie', visible: false },
        { name: 'bookstoreClassification', text: 'Varegruppe', visible: false, foreignKeyType: 'productCategory' },
        { name: 'categories', text: 'Kategorier', visible: false },
        { name: 'physicalQty', text: 'Fysisk antal', format: 'Number' },
        { name: 'reservedQty', text: 'Reserveret antal', format: 'Number' },
        { name: 'remainQty', text: 'Resterende antal', format: 'Number', orderBy: 'Ascending', onQueryCellInfo: OnHandCellInfo.availableQty, visible: false },
        { name: 'unreservedQty', text: 'Ej reserveret antal', format: 'Number', visible: false },
        { name: 'availableQty', text: 'Disponibelt antal', format: 'Number', orderBy: 'Ascending', orderByPriority: 1, onQueryCellInfo: OnHandCellInfo.availableQty },
        { name: 'updated', text: 'Opdateret', format: 'DateShort', visible: false },
        { name: 'created', text: 'Rest opdateret', format: 'DateShort', visible: false },
        { name: 'publishingDate', text: 'Udgivet dato', tooltipText: 'Udgivet dato fra Bogportalen', format: 'DateShort', orderBy: 'Ascending', orderByPriority: 2, onQueryCellInfo: OnHandCellInfo.publishingDate },
        { name: 'publishedDays', text: 'Udgivet dage', tooltipText: 'Antal dage udgivet fra Bogportalen', format: 'Integer', visible: false, onQueryCellInfo: OnHandCellInfo.publishingDate },
        { name: 'isPublished', text: 'Udgivet', format: 'CheckBox', onQueryCellInfo: OnHandCellInfo.isPublished },
        { name: 'buyable', text: 'Lagerføres', format: 'CheckBox', filterValue: 1, filterOperator: 'equal', filterOnClient: true, onQueryCellInfo: OnHandCellInfo.buyable },
        { name: 'buyableTrade', text: 'Kan købes', format: 'CheckBox', onQueryCellInfo: OnHandCellInfo.buyableTrade },
        { name: 'apologyCode', text: 'Undskyldningskode', format: 'NumberLeft', widthType: FieldWidthEnum.XS, onQueryCellInfo: OnHandCellInfo.apologyCode, visible: false },
        { name: 'sourcingMinOnhandQty', text: 'Min. lager', format: 'Integer', onQueryCellInfo: OnHandCellInfo.sourcingMinOnhandQty },
        { name: 'minimumInventoryPct', text: '% min. lager', format: 'IntegerPct', onQueryCellInfo: OnHandCellInfo.sourcingMinOnhandQty },
        { name: 'warehouseItemNum', text: 'Lager varenr.', visible: false },
        { name: 'originalTitle', text: 'Orig. titel', visible: false },
        { name: 'originalLanguage', text: 'Orig. sprog', visible: false },
        { name: 'authors', text: 'Forfatter', visible: false },
        { name: 'statusCode', text: 'Status', visible: false },
        { name: 'salesPrice', text: 'Salgspris', format: 'Integer', visible: false },
        { name: 'modifiedJobId', text: 'Rest opdateret job', format: 'Number', visible: false }
      ]
    });
  }

  public onCommand(event: UiCommandEvent) {
    if (event.actionType == UiActionTypeEnum.DataBound) {
      var data: any[] = event.args?.result;
      if (event.appComponent && data && data.length > 0) {
        this.setupUpdatedDate(<CerGridComponent>event.appComponent, new Date(data[0].updated));
      }
    }
  }

  private setupUpdatedDate(grid: CerGridComponent, updatedDate: Date) {
    var text: string = 'Opdateret ' + formatDateDifference(updatedDate, new Date());
    var button: HTMLButtonElement = grid.commandService.toolbarButtonGet(updatedDateCmd.tooltipText);
    if (button) {
      grid.commandService.toolbarButtonTextSet(button, text);
    }
  }
}

export class OnHandCellInfo {

  public static availableQty(args: any) {
    var dto: any = args.data;

    if (dto.publishedDate > new Date() || dto.buyable != true || dto.apologyCode >= 500) {
      return;
    }

    if ((dto.availableQty <= 0)) {
      ColorCss.backgroundClass(args.cell, 'red');
    }
    else if (dto.minimumInventoryPct && dto.minimumInventoryPct < 100) {
      ColorCss.backgroundClass(args.cell, 'yellow');
    }
  }

    public static publishingDate(args: any) {
    var dto: any = args.data;

    if (dto.publishingDate >= new Date() || dto.publishingDate == null) {
      ColorCss.backgroundClass(args.cell, 'green');
    }
  }

  public static publishedDate(args: any) {
    var dto: any = args.data;

    if (dto.publishedDate >= new Date() || dto.publishedDate == null) {
      ColorCss.backgroundClass(args.cell, 'green');
    }
  }

  public static isPublished(args: any) {
    var dto: any = args.data;

    if (dto.isPublished != true) {
      ColorCss.backgroundClass(args.cell, 'green');
    }
  }

  public static published(args: any) {
    var dto: any = args.data;

    if (dto.published != true) {
      ColorCss.backgroundClass(args.cell, 'green');
    }
  }

  public static buyable(args: any) {
    var dto: any = args.data;

    if (dto.buyable != true) {
      ColorCss.backgroundClass(args.cell, 'yellow');
    }
  }

  public static buyableTrade(args: any) {
    var dto: any = args.data;

    if (dto.buyableTrade != true) {
      ColorCss.backgroundClass(args.cell, 'yellow');
    }
  }

  public static plLeder(args: any) {
    var dto: any = args.data;

    if (dto.apologyCode >= 500) {
      ColorCss.backgroundClass(args.cell, 'yellow');
    }
  }

  public static apologyCode(args: any) {
    var dto: any = args.data;

    if (dto.apologyCode >= 500) {
      ColorCss.backgroundClass(args.cell, 'yellow');
    }
  }

  public static sourcingMinOnhandQty(args: any) {
    var dto: any = args.data;

    if ((dto.publishedDate ?? dto.publishingDate) <= new Date() && dto.buyable) {
      var min = dto.sourcingMinOnhandQty ?? dto.minimumInventory;
      if (min > 0 && dto.minimumInventoryPct < 100) {
        ColorCss.backgroundClass(args.cell,(dto.availableQty <= 0) ? 'red' : 'yellow');
      }
    }
  }
  /*
  Tolkning på om vare kan vises og købes
  --------------------------------------
  En vare kan købes hvis:
  Varen skal være til salg med en aktiv købspris større end nul. Samtidig skal der være lager eller en
  undskyldningskode mindre end 500.
  Det kan omsættes til, at følgende 3 bolske udtryk skal være sande:
  buyable = 1
  amountInSmallestUnit > 0
  approximateInventoryNumber > 0 ELLER ApologyCode < 500
  *bemærk hvis felter validFrom og validTo har værdi, så skal dagsdato ligge indenfor deres interval før en
  pris er aktuel.
  Eksportfunktionaliteten i systemet genere kun nye produkter(XML) der er ændret siden sidste kørsel og
  altid med den aktuelle pris for dagen i feltet <price>
  Ved flere priser findes listen <priceDataList> med priser og gyldighedsdatoer som bruges til at bestemme
  den aktuelle pris.
  *bemærk at et forlag kan sætte forceVisible = false, det betyder de ikke vil have bogen vist
*/
}
